import { SetStateAction, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { SettingsOutlineIcon } from "@dotnet/telematics-design-system";

import CompanyLevelDialog from "components/CompanyLevel";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import AuthContext from "context/auth/authContext";
import { MenuAnchorEl, useMediaQueries } from "shared";

import ApiTokensDialog from "./ApiTokens";
import BannerManagementDialog from "./BannerManagement";
import SecurityAndAccountDialog from "./SecurityAndAccount";
import SettingsContent from "./SettingsContent";
import SettingsMenuDesktop from "./SettingsMenuDesktop";
import { DIALOG_NAMES, SettingsProps } from "./types";

const Settings = ({ handleDrawer }: SettingsProps) => {
    const { user } = useContext(AuthContext);

    const { t } = useTranslation();
    const { fromMd } = useMediaQueries();

    const [anchorEl, setAnchorEl] = useState<MenuAnchorEl>(null);

    const [isDialogOpen, setIsDialogOpen] = useState({
        [DIALOG_NAMES.ApiTokens]: false,
        [DIALOG_NAMES.BannerManagement]: false,
        [DIALOG_NAMES.CompanyLevel]: false,
        [DIALOG_NAMES.SecurityAndAccount]: false
    });

    const openMenu = (e: { currentTarget: SetStateAction<MenuAnchorEl> }) =>
        setAnchorEl(e.currentTarget);

    const closeMenu = () => setAnchorEl(null);

    const openDialog = (name: DIALOG_NAMES) => {
        closeMenu();
        setIsDialogOpen({ ...isDialogOpen, [name]: true });
    };

    const closeDialog = () => {
        handleDrawer?.(false);

        setIsDialogOpen({
            [DIALOG_NAMES.ApiTokens]: false,
            [DIALOG_NAMES.BannerManagement]: false,
            [DIALOG_NAMES.CompanyLevel]: false,
            [DIALOG_NAMES.SecurityAndAccount]: false
        });
    };

    const { apiTokens, bannerManagement, companyLevel, securityAndAccount } =
        isDialogOpen;

    const canManageSecurityAndAccountSettings = Boolean(
        user?.password_auth_enabled
    );

    return (
        <>
            {fromMd && (
                <Button
                    color={BUTTON_COLORS.White}
                    size={BUTTON_SIZES.Small}
                    variant={BUTTON_VARIANTS.IconLeft}
                    icon={<SettingsOutlineIcon />}
                    onClick={openMenu}
                >
                    {t("Button##settings")}
                </Button>
            )}

            {fromMd ? (
                <SettingsMenuDesktop
                    anchorEl={anchorEl}
                    close={closeMenu}
                    openDialog={openDialog}
                />
            ) : (
                <SettingsContent
                    openDialog={openDialog}
                    closeMenu={closeMenu}
                />
            )}

            <ApiTokensDialog isOpen={apiTokens} close={closeDialog} />

            <BannerManagementDialog
                isOpen={bannerManagement}
                close={closeDialog}
            />

            <CompanyLevelDialog isOpen={companyLevel} close={closeDialog} />

            {canManageSecurityAndAccountSettings && (
                <SecurityAndAccountDialog
                    isOpen={securityAndAccount}
                    close={closeDialog}
                />
            )}
        </>
    );
};

export default Settings;
